// Shared rendering rules for creator data.
//
// Every helper here exists to answer one question honestly: how much do we
// actually know? The catalogue mixes profiles pulled from Instagram's public
// API with profiles a creator wrote themselves, and the difference has to reach
// the screen. Presenting an unknown rate as "gifting only", or official
// platform figures as "unverified", both mislead — in opposite directions.

import { BadgeCheck, FlaskConical, Globe, ShieldAlert } from "lucide-react";
import { Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { formatMoney } from "@/lib/format";
import type { CreatorProfile, SocialAccount } from "@/hooks/useMarketplace";

/**
 * The handle to show a human.
 *
 * `CreatorProfile.handle` is namespaced (`instagram-fedevalverde`) so a
 * discovered profile can never collide with another platform or squat a handle
 * a real creator might want. That prefix is plumbing and must not be shown —
 * the social handle is what a brand recognises and can search for.
 */
export function displayHandle(creator: CreatorProfile): string {
  const primary = creator.socialAccounts[0];
  if (primary?.handle) return primary.handle;
  return creator.handle.replace(/^(instagram|tiktok|youtube|x)-/, "");
}

/** Initial for the avatar fallback. */
export function initialFor(creator: CreatorProfile): string {
  return (creator.displayName || creator.handle).trim().slice(0, 1).toUpperCase();
}

export type ProvenanceTone = "verified" | "public" | "self" | "fixture";

export function provenanceOf(account: SocialAccount | null | undefined): ProvenanceTone {
  if (!account) return "self";
  if (account.dataSource === "OAUTH_VERIFIED") return "verified";
  if (account.dataSource === "PUBLIC_API") return "public";
  if (account.dataSource === "FIXTURE") return "fixture";
  return "self";
}

const TONE_STYLES: Record<ProvenanceTone, string> = {
  verified: "text-steel-600 bg-steel-50 border-steel-100",
  // Neutral, not amber. Public API data is accurate — flagging it as a warning
  // would tell a brand to distrust the most reliable figures in the catalogue.
  public: "text-neutral-600 bg-neutral-100 border-transparent",
  self: "text-warning-fg bg-warning-bg border-warning-border",
  // Red, and louder than "self". Self-reported numbers are a real person's
  // claim about themselves; fixture numbers are about nobody at all, and
  // showing them to an agency that knows these creators by name is the one
  // mistake here that costs credibility outright.
  fixture: "text-danger-fg bg-danger-bg border-danger-border",
};

/**
 * The same four tones as they read on top of a creator's photograph, where the
 * card behind them is an image rather than white. `verified` inverts to a solid
 * steel stamp; everything else sits on near-opaque white so the warning colours
 * survive whatever is underneath.
 */
const ON_IMAGE_STYLES: Record<ProvenanceTone, string> = {
  verified: "text-white bg-steel-600 border-transparent",
  public: "text-neutral-700 bg-white/90 border-transparent backdrop-blur-[8px]",
  self: "text-warning-fg bg-white/92 border-transparent backdrop-blur-[8px]",
  fixture: "text-danger-fg bg-white/92 border-transparent backdrop-blur-[8px]",
};

const TONE_KEYS: Record<ProvenanceTone, { label: string; hint: string }> = {
  verified: {
    label: "marketplace.provenance.verified",
    hint: "marketplace.provenance.verifiedHint",
  },
  public: { label: "marketplace.provenance.public", hint: "marketplace.provenance.publicHint" },
  self: { label: "marketplace.provenance.self", hint: "marketplace.provenance.selfHint" },
  fixture: {
    label: "marketplace.provenance.fixture",
    hint: "marketplace.provenance.fixtureHint",
  },
};

/**
 * The single renderer for "where did this number come from".
 *
 * The hint is a real tooltip rather than a `title` attribute: `title` takes a
 * second to appear, cannot be styled, and never shows on touch — which made the
 * one explanation of why a figure might be invented the hardest thing on the
 * card to reach.
 *
 * Stroke weight rises as the glyph shrinks. Keeping it at 2 makes the 9px tick
 * inside a stamp disappear entirely.
 */
export function ProvenanceBadge({
  account,
  t,
  compact = false,
  onImage = false,
}: {
  account: SocialAccount | null | undefined;
  t: (key: string) => string;
  compact?: boolean;
  /** Sitting on a creator portrait rather than on a white card. */
  onImage?: boolean;
}) {
  const tone = provenanceOf(account);
  const { label, hint } = TONE_KEYS[tone];
  const Icon =
    tone === "verified"
      ? BadgeCheck
      : tone === "public"
        ? Globe
        : tone === "fixture"
          ? FlaskConical
          : ShieldAlert;

  return (
    <Tooltip label={t(hint)}>
      <span
        className={cn(
          "inline-flex items-center gap-1 whitespace-nowrap rounded-pill border font-bold uppercase tracking-[0.06em]",
          onImage ? ON_IMAGE_STYLES[tone] : TONE_STYLES[tone],
          compact ? "px-1.5 py-px text-label-sm" : "px-2 py-1 text-label-sm",
        )}
      >
        <Icon className={compact ? "size-2.5" : "size-3"} strokeWidth={compact ? 3 : 2.6} />
        {t(label)}
      </span>
    </Tooltip>
  );
}

/**
 * What to print where a rate would go.
 *
 * A discovered profile has no rate, because nobody has asked the creator. That
 * is not the same as "works for free" — and a brand reading "gifting only"
 * would approach them on entirely the wrong footing.
 */
export function rateLabel(
  creator: CreatorProfile,
  t: (key: string) => string,
): { text: string; known: boolean } {
  if (creator.minRate !== null && creator.minRate > 0) {
    // Currency code before the figure, Spanish thousands separator: `USD 4.150`.
    return { text: formatMoney(creator.minRate, creator.baseCurrency)!, known: true };
  }
  // Only a creator who owns their profile can tell us they work for gifting.
  if (creator.source === "SELF_REGISTERED" && creator.acceptsGifting) {
    return { text: t("marketplace.creator.onlyGifting"), known: true };
  }
  return { text: t("marketplace.creator.rateUnknown"), known: false };
}

/**
 * Avatar with a graceful fallback.
 *
 * Instagram's profile_picture_url is a signed CDN link that expires, so these
 * will 403 eventually. The initial-circle fallback means that degrades to what
 * the UI looked like before rather than to a broken-image icon. Caching the
 * images ourselves is the real fix, when it matters.
 */
export function CreatorAvatar({
  creator,
  size = 44,
  failed,
  onFail,
}: {
  creator: CreatorProfile;
  size?: number;
  failed: boolean;
  onFail: () => void;
}) {
  const shared = "rounded-full flex-shrink-0 object-cover";

  if (creator.avatarUrl && !failed) {
    return (
      <img
        src={creator.avatarUrl}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={onFail}
        className={`${shared} bg-neutral-100`}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      className={`${shared} bg-gradient-to-br from-steel-600 to-steel-400 text-white flex items-center justify-center font-bold`}
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initialFor(creator)}
    </div>
  );
}
